import { ReactNode } from 'react'
import { atom, DefaultValue, selector, selectorFamily, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { userState, useUser } from '../atoms'
import { SegmentOrderKeyType } from '../atoms/homeStatus'
import { User } from '../models/User'
import { HistorySegment } from '../components/HistorySegment'
import HomeSegment from '../components/HomeSegment'
import { LinkSegment } from '../components/LinkSegment'
import { NewsSegment } from '../components/NewsSegment'
import PreferitiSegment from '../components/PreferitiSegment'
import { logorder } from './homePageReorder'
import _ from 'lodash'
import NotaOperativaSegment from '../components/NotaOperativaSegment'

// Per visualizzare una drop zone sotto tutti i segmenti ci serve un segmento fantasma
export const DropSegment = () => <HomeSegment title='Drop' />

export type HomePageConfigItemBaseType = {
    name: SegmentOrderKeyType,
    order: number,
    pinned: boolean,
    open: boolean,
    dummy?: boolean,
}

export type HomePageConfigItemType = {
    config: HomePageConfigItemBaseType,
    element: ReactNode,
}

export const homePageConfigDefault: HomePageConfigItemBaseType[] = [
    { name: 'Preferiti', order: 1, pinned: false, open: true, },
    { name: 'Link', order: 2, pinned: false, open: true, },
    { name: 'History', order: 3, pinned: false, open: true, },
    { name: 'News', order: 4, pinned: false, open: true, },
    { name: 'NotaOperativa', order: 5, pinned: false, open: true, },
    { name: 'Drop', order: 6, pinned: false, open: true, dummy: true },
]
const homePageSegments: Record<string, ReactNode> = {
    "Preferiti": <PreferitiSegment key={'preferiti'} />,
    "Link": <LinkSegment key={'link'} />,
    "History": <HistorySegment key={'history'} />,
    "News": <NewsSegment key={'news'} />,
    "NotaOperativa": <NotaOperativaSegment key={'nota'} />,
    "Drop": <DropSegment key={'drop'} />,
}

const getHomePageConfig = (base: HomePageConfigItemBaseType[]) => {
    return base.map<HomePageConfigItemType>(c => ({ config: c, element: homePageSegments[c.name] }))
}
const getHomePageConfigDefault = () => getHomePageConfig(homePageConfigDefault)

// Il selettore da cui dipende, per l'inizializzazione l'atomo serve per leggere 
// la configurazione in localStorage
const keySuffix = "homePageConfig"
const getKey = (user: User | null) => user ? `-${user.name}--${keySuffix}` : keySuffix

const saveConfigToLocalStorage = (key: string, config: HomePageConfigItemType[]) => {
    const toSerialize = config.map(x => (x.config))
    const serialized = JSON.stringify(toSerialize)
    window.localStorage.setItem(key, serialized)
}
// Questo selettore è utilizzato per inizializzare l'atom che tiene lo stato della configurazione
// della HomePage
// Legge lo stato da localStorage e, se non lo trova, ne fornisce uno di default
const homePageConfigDefaultSelector = selector({
    key: 'homePageConfigDefaultSelector',
    get: ({ get }) => {
        // console.log('homePageConfigDefaultSelector:get => retrieving configuration')
        const user = get(userState)
        const key = getKey(user)
        try {
            const temp = window.localStorage.getItem(key)
            if (temp) {
                let deserialized: HomePageConfigItemBaseType[] = JSON.parse(temp)
                let didAnOperation = false
                homePageConfigDefault.forEach(d => {
                    if(!deserialized.find(x => x.name === d.name)) {
                        deserialized = [...deserialized, d]
                        didAnOperation = true
                    }
                })
                if(didAnOperation) {
                    deserialized = [...(deserialized.filter(x => x.name !== 'Drop')), homePageConfigDefault[homePageConfigDefault.length - 1]]
                }
                const rv = getHomePageConfig(deserialized)
                // console.log('homePageConfigDefaultSelector: Home Page Configuration Retrieved', rv)
                return rv
            } else {
                const rv = getHomePageConfigDefault()
                saveConfigToLocalStorage(key, rv)
                return rv
            }
        } catch (e) {
            console.error(`homePageConfigDefaultSelector:get => ${e}`)
        }
        return getHomePageConfigDefault()
    }
})

// Questo atomo contiene la configurazione run-time della pagina di login
// di questa una parte è salvabile (perché serializzabile), mentre il nodo
// React viene appeso all'inizializzazione
const homePageConfigAtom = atom({
    key: 'homePageConfigAtom',
    default: homePageConfigDefaultSelector,
    effects: [
        ({ onSet }) => {
            onSet(newValue => {
                console.log(`%chomePageConfigAtom:effect => onSet ${logorder(newValue)} newValue = %o`, 'color: green; font-weight: bold;', newValue)
            })
        }
    ]
})

export const homePageConfigDicState = selector({
    key: 'homePageConfigDicState',
    get: ({ get }) => {
        const hpc = get(homePageConfigAtom)
        return hpc.reduce((p, c) => { p[c.config.name] = c; return p; }, {} as Record<SegmentOrderKeyType, HomePageConfigItemType>)
    }
})

export const useSetHomePageConfig = () => {
    const setConfig = useSetRecoilState(homePageConfigAtom)
    const user = useUser()
    const key = getKey(user)

    return (newValue: HomePageConfigItemType[]) => {
        setConfig(prev => {
            // Dizionario per indicizzare per nome gli elementi del nuovo valore
            const dic = newValue.reduce((p, c) => { p[c.config.name] = c; return p; }, {} as Record<SegmentOrderKeyType, HomePageConfigItemType>)
            const rv = _.cloneDeep(prev)
            rv.forEach(x => x.config.order = dic[x.config.name].config.order)
            saveConfigToLocalStorage(key, rv)
            return rv
        })
    }

}

export const homeSegmentConfigurationState = selectorFamily<HomePageConfigItemBaseType, SegmentOrderKeyType>({
    key: 'homeSegmentConfigurationState',
    get: (title: SegmentOrderKeyType) => ({ get }) => {
        const homePageConfig = get(homePageConfigAtom)
        const rv = homePageConfig.find(x => x.config.name === title) ?? { element: {}, config: { name: 'Drop', open: false, pinned: false, order: 0, dummy: true } }
        return rv.config
    },
    set: (title: SegmentOrderKeyType) => ({ get, set }, value: HomePageConfigItemBaseType | DefaultValue) => {
        if (value instanceof DefaultValue) return
        const user = get(userState)
        const key = getKey(user)
        // console.log(`%chomeSegmentConfigurationState:set => setting config for ${title}`, 'color: magenta; background-color: #e0e0e0; padding: 2px;')
        set(homePageConfigAtom, prevValue => {
            const rv = [...prevValue]
            const index = rv.findIndex(x => x.config.name === title)
            rv[index] = { ...rv[index], config: { ...value } }
            // Salviamo la configurazione in localStorage
            saveConfigToLocalStorage(key, rv)
            return rv
        })
    }
})

export const useHomePageConfiguration = () => {
    const config = useRecoilValue(homePageConfigAtom)
    return config
}

// Qui mettiamo il salvataggio in local storage dello stato duranta la fase di
// modifica dello stato
export const useHomeSegmentConfiguration = (title: SegmentOrderKeyType) => {
    return useRecoilState(homeSegmentConfigurationState(title))
}

export const reorderSegment = (from: string, to: string) => {
    // Deve riordinare i segmenti in modo da rispettare le seguenti regole:
    // to: è il componente drop
    // from: è il componente drag
    //
    // i componenti pinned vanno all'inizio e non accettano nessun drop (nessuna riga azzurra: {!pinned && <RigaAzzurra />})
    // il componente dummy (Drop) deve restare dov'è
    // se un componente viene droppato su se stesso non si muove
    // se il componente in discesa viene droppato sul successivo non si muove
    // il componente in salita si muove rispettando le regole precedenti
}
